import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Alert,
} from "@mui/material";
import PublishIcon from "@mui/icons-material/Publish";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import RefreshIcon from "@mui/icons-material/Refresh";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const LeagueStandings = ({ leagueEvent, onLeagueEventUpdate }) => {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(false);

  const leagueEventId = leagueEvent?._id;
  const isManual = leagueEvent?.standingsMode === "manual";
  const isPublished = leagueEvent?.standingsPublished;

  useEffect(() => {
    if (leagueEventId) loadStandings();
  }, [leagueEventId]);

  const loadStandings = async () => {
    setLoading(true);
    const result = await request(`leagueevent/standings?leagueEventId=${leagueEventId}`);
    if (result?.success) {
      setStandings(result.data);
    }
    setLoading(false);
  };

  const handlePublish = async () => {
    const result = await request("leagueevent/standings/publish", "post", { leagueEventId });
    if (result?.success) {
      toast.success("Standings published — players can now see them");
      onLeagueEventUpdate(result.data);
    }
  };

  const handleUnpublish = async () => {
    const result = await request("leagueevent/standings/unpublish", "post", { leagueEventId });
    if (result?.success) {
      toast.success("Standings unpublished");
      onLeagueEventUpdate(result.data);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6">Standings</Typography>
          {isManual && (
            <Chip
              label={isPublished ? "Published" : "Unpublished"}
              color={isPublished ? "success" : "default"}
              size="small"
            />
          )}
          {!isManual && (
            <Chip label="Live" color="success" size="small" />
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" startIcon={<RefreshIcon />} onClick={loadStandings}>
            Refresh
          </Button>
          {isManual && !isPublished && (
            <Button
              variant="contained"
              size="small"
              startIcon={<PublishIcon />}
              onClick={handlePublish}
            >
              Publish Standings
            </Button>
          )}
          {isManual && isPublished && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<UnpublishedIcon />}
              onClick={handleUnpublish}
            >
              Unpublish
            </Button>
          )}
        </Box>
      </Box>

      {isManual && !isPublished && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Standings are in manual mode. Players cannot see standings until you click "Publish Standings".
        </Alert>
      )}

      {loading && (
        <Typography variant="body2" color="text.secondary">Loading standings…</Typography>
      )}

      {!loading && standings.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No standings yet — record some game scores first.
        </Typography>
      )}

      {standings.map(({ division, teams }) => (
        <Box key={division} sx={{ mb: 4 }}>
          {standings.length > 1 && (
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
              {division}
            </Typography>
          )}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40}>#</TableCell>
                  <TableCell>Team</TableCell>
                  <TableCell align="center">GP</TableCell>
                  <TableCell align="center">W</TableCell>
                  <TableCell align="center">L</TableCell>
                  <TableCell align="center">T</TableCell>
                  <TableCell align="center">Pts Diff</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teams.map((row) => (
                  <TableRow key={row.team}>
                    <TableCell>{row.position}</TableCell>
                    <TableCell>{row.teamName}</TableCell>
                    <TableCell align="center">{row.gamesPlayed}</TableCell>
                    <TableCell align="center">{row.wins}</TableCell>
                    <TableCell align="center">{row.losses}</TableCell>
                    <TableCell align="center">{row.ties}</TableCell>
                    <TableCell align="center" sx={{ color: row.pointDiff >= 0 ? "success.main" : "error.main" }}>
                      {row.pointDiff >= 0 ? `+${row.pointDiff}` : row.pointDiff}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Box>
  );
};

export default LeagueStandings;
